import React, { createContext, useState, useEffect, useCallback, PropsWithChildren, useMemo } from 'react';

import * as testsService from '../services/api/tests';
import { OrderView, OrderViewSource, OrderViewSourceGroup } from '../entities/order-view';
import { useAuth } from '../hooks/use-auth';
import { Test } from '../entities/test';

interface ContextValue {
  testView?: OrderView & { test: Test };
  setTestId: (id: number) => void;
  updateTest: (test: Test) => void;
  refreshTest: () => Promise<void>;
  updateSourceGroups: (sourceGroups: OrderViewSourceGroup[]) => void;
  updateSourceGroup: (sourceGroup: OrderViewSourceGroup, data: Partial<OrderViewSourceGroup>) => void;
  updateSource: (sourceGroup: OrderViewSourceGroup, source: OrderViewSource, data: Partial<OrderViewSource>) => void;
  removeSourceGroup: (sourceGroup: OrderViewSourceGroup) => void;
  removeSource: (sourceGroup: OrderViewSourceGroup, source: OrderViewSource) => void;
  addSourceGroup: (sourceGroup: OrderViewSourceGroup) => void;
  addSource: (sourceGroup: OrderViewSourceGroup, source: OrderViewSource) => void;
}

const TestContext = createContext<ContextValue>({
  testView: undefined,
  setTestId: () => null,
  updateTest: () => null,
  refreshTest: () => Promise.resolve(),
  updateSourceGroups: () => null,
  updateSourceGroup: () => null,
  updateSource: () => null,
  removeSourceGroup: () => null,
  removeSource: () => null,
  addSourceGroup: () => null,
  addSource: () => null,
});

function TestContextProvider({ children }: PropsWithChildren<object>) {
  const [testId, setTestId] = useState<number>();
  const [testView, setTestView] = useState<OrderView & { test: Test }>();
  const { token } = useAuth();

  useEffect(() => {
    const fetchData = async (id: number) => {
      const item = await testsService.view(token, id);

      setTestView(item);
    };

    if (testId && !testView) {
      fetchData(testId);
    }
  }, [token, testView, testId, setTestView]);

  const updateTest = useCallback(
    (newTest: Test) => {
      if (testView) {
        setTestView({ ...testView, test: newTest });
      }
    },
    [testView],
  );

  const refreshTest = useCallback(async () => {
    if (testId) {
      const freshTest = await testsService.view(token, testId);

      setTestView(freshTest);
    }
  }, [token, testId]);

  const updateSourceGroups = useCallback(
    (sourceGroups: OrderViewSourceGroup[]) => {
      if (testView) {
        setTestView({ ...testView, sourceGroups });
      }
    },
    [testView],
  );

  const updateSourceGroup = useCallback(
    (sourceGroup: OrderViewSourceGroup, data: Partial<OrderViewSourceGroup>) => {
      if (testView) {
        const idx = testView.sourceGroups.indexOf(sourceGroup);

        testView.sourceGroups[idx] = { ...testView.sourceGroups[idx], ...data };

        if (idx !== -1) {
          setTestView({
            ...testView,
          });
        }
      }
    },
    [setTestView, testView],
  );

  const updateSource = useCallback(
    (sourceGroup: OrderViewSourceGroup, source: OrderViewSource, data: Partial<OrderViewSource>) => {
      if (testView) {
        const groupIndex = testView.sourceGroups.indexOf(sourceGroup);

        if (groupIndex !== -1) {
          const sourceIndex = testView.sourceGroups[groupIndex].sources.indexOf(source);

          if (sourceIndex !== -1) {
            testView.sourceGroups[groupIndex].sources[sourceIndex] = {
              ...testView.sourceGroups[groupIndex].sources[sourceIndex],
              ...data,
            };

            setTestView({ ...testView });
          }
        }
      }
    },
    [setTestView, testView],
  );

  const removeSourceGroup = useCallback(
    (sourceGroup: OrderViewSourceGroup) => {
      if (testView) {
        const idx = testView.sourceGroups.indexOf(sourceGroup);

        if (idx !== -1) {
          testView.sourceGroups.splice(idx, 1);

          setTestView({ ...testView });
        }
      }
    },
    [setTestView, testView],
  );

  const removeSource = useCallback(
    (sourceGroup: OrderViewSourceGroup, source: OrderViewSource) => {
      if (testView) {
        const groupIndex = testView.sourceGroups.indexOf(sourceGroup);

        if (groupIndex !== -1) {
          const sourceIndex = testView.sourceGroups[groupIndex].sources.indexOf(source);

          if (sourceIndex !== -1) {
            testView.sourceGroups[groupIndex].sources.splice(sourceIndex, 1);

            setTestView({ ...testView });
          }
        }
      }
    },
    [setTestView, testView],
  );

  const addSourceGroup = useCallback(
    (sourceGroup: OrderViewSourceGroup) => {
      if (testView) {
        testView.sourceGroups.push(sourceGroup);

        setTestView({ ...testView });
      }
    },
    [setTestView, testView],
  );

  const addSource = useCallback(
    (sourceGroup: OrderViewSourceGroup, source: OrderViewSource) => {
      if (testView) {
        const groupIndex = testView.sourceGroups.indexOf(sourceGroup);

        if (groupIndex !== -1) {
          testView.sourceGroups[groupIndex].sources.push(source);

          setTestView({ ...testView });
        }
      }
    },
    [setTestView, testView],
  );

  const value = useMemo(
    () => ({
      testView,
      setTestId,
      updateTest,
      refreshTest,
      updateSourceGroups,
      updateSourceGroup,
      updateSource,
      removeSourceGroup,
      removeSource,
      addSourceGroup,
      addSource,
    }),
    [
      testView,
      setTestId,
      updateTest,
      refreshTest,
      updateSourceGroups,
      updateSourceGroup,
      updateSource,
      removeSourceGroup,
      removeSource,
      addSourceGroup,
      addSource,
    ],
  );

  return <TestContext.Provider value={value}>{children}</TestContext.Provider>;
}

const useTest = () => {
  const context = React.useContext(TestContext);

  if (context === undefined) {
    throw new Error('useTest must be used within a TestContextProvider');
  }

  return context;
};

export { TestContextProvider, useTest };
